






































































































































import {
    defineComponent,
    computed,
    PropType,
    useRoute,
    ref,
    watch,
    inject,
    Ref,
} from "@nuxtjs/composition-api";
import { Product } from "@wemade-vsf/catalog/lib/runtime/product/types";
import { prepareCardProduct } from "@wemade-vsf/catalog/lib/runtime/product/helpers/prepareCardProduct";
import { useImage } from "@wemade-vsf/composables";
import ProductPrice from "components/catalog/Product/ProductPrice.vue";
import {
    formatNetRegularPrice,
    formatRegularPriceString,
} from "~/stores/formatPrice";
import { useCustomerPricing } from "@wemade-vsf/catalog/lib/runtime/product/composables/useCustomerPricing";
import type { Price } from "@wemade-vsf/catalog/lib/runtime/types";
import SkeletonLoader from "components/theme/SkeletonLoader.vue";
import { WmSwatchData } from "~/composables/useDeliverySwatchData";
import { useCart } from "@wemade-vsf/customer";
import type { Cart } from "@wemade-vsf/magento-api";

export default defineComponent({
    name: "ProductCard",
    props: {
        product: {
            type: Object as PropType<Product>,
            required: true,
        },
        showPrice: {
            type: Boolean,
            default: true,
        },
        showActions: {
            type: [Boolean, Array],
            default: false,
        },
        cart: {
            type: Object as PropType<Cart>,
            default: null,
        },
        deliverySwatchData: {
            type: Array as PropType<WmSwatchData[]>,
            default: null,
        },
    },
    components: {
        ProductPrice,
        ProductCardActions: () =>
            import("components/catalog/Product/ProductCard/Actions.vue"),
        ProductLabels: () =>
            import("components/catalog/Product/ProductLabels.vue"),
        SkeletonLoader,
    },
    setup(props, { emit }) {
        const {
            addItem,
            removeItem,
            error: cartError,
            loading: isCartLoading,
            canAddToCart,
            updateItemQty,
        } = useCart();
        const {
            imageSizes: { productCard: imageSize },
        } = useImage();
        const cardProduct = prepareCardProduct(props.product);
        const {
            title,
            image,
            link,
            nuxtImgConfig,
            specialPrice,
            regularPrice,
            hasPriceRange,
            price,
            priceIncludesTax,
        } = cardProduct.cardProps;

        const { sku } = cardProduct;
        const hasLabels = !!props.product.product_labels?.length;
        const availableActions = [
            "link",
            ...(cardProduct.cardProps.showCartButton ? ["cart"] : []),
        ];

        const { getNetPrice, getPriceForProduct } = useCustomerPricing();

        const taxOff = inject("taxOff") as Ref<boolean>;

        const recommendedNetPrice = computed<Price | null>(() => {
            if (!price) return null;

            const priceRaw = getNetPrice(price, props.product.applied_tax_rate);

            return priceRaw;
        });

        const displayRegularPrice = computed(() => {
            if (!recommendedNetPrice.value?.regular) {
                return null;
            }
            const value = taxOff.value
                ? formatNetRegularPrice(recommendedNetPrice.value?.regular)
                : formatRegularPriceString(regularPrice);
            return value;
        });

        const displaySpecialPrice = computed(() => {
            const value = taxOff.value
                ? recommendedNetPrice.value?.special || null
                : specialPrice;

            return value;
        });

        const triggerClick = () => {
            emit("clicked:product", props.product.sku);
        };

        const qty = ref<Number>(0);
        const cartProduct = ref(null);
        watch(
            () => props.cart,
            (cart) => {
                const product = ((cart && cart.items) || []).find(
                    (item) => item.product.uid === props.product.uid
                );

                cartProduct.value = product;

                qty.value = product ? product.quantity : 0;
            }
        );

        const deliveryTime = computed(() => {
            const productStockAttribute = (
                props.product.wm_custom_attributes || []
            ).find((item) => item.attribute_code === "wm_delivery_time");

            if (productStockAttribute) {
                return (props.deliverySwatchData || []).find(
                    (item) => item.label === productStockAttribute.value
                );
            }
            return null;
        });

        const showAddToCart = computed(
            () =>
                cardProduct.cardProps.showCartButton &&
                ![
                    "ConfigurableProduct",
                    "BundleProduct",
                    "GroupedProduct",
                ].includes(props.product.__typename)
        );

        const addToCartError = computed(
            () => cartError.value?.addItem?.message
        );
        const route = useRoute();
        const { finder, brand, model } = route.value.params;

        if (finder && brand && model) {
            link.query = {
                ...link.query,
                pfa: [finder, brand, model].join(","),
            };
        }

        const handleInput = async (newQuantity) => {
            if (newQuantity <= 0) {
                await removeItem({
                    product: cartProduct.value,
                });
                qty.value = 0;
                return;
            }

            if (!cartProduct.value) {
                await handleAddToCart();
                return;
            }

            await updateItemQty({
                product: cartProduct.value,
                quantity: newQuantity,
            });
            qty.value = newQuantity;
        };

        const handleAddToCart = async () => {
            await addItem({
                product: props.product,
                quantity: 1,
            });
            qty.value = 1;
        };

        const isNew = computed(() => {
            return props.product.updated_at
                ? new Date(props.product.updated_at) >
                      new Date(Date.now() - 1000 * 60 * 60 * 24 * 15)
                : false;
        });

        const rawPrice = getPriceForProduct(props.product);
        const isSale = computed(() => {
            if (!rawPrice.special) return 0;

            const difference = rawPrice.regular - rawPrice.special;

            const factor = difference / rawPrice.regular;

            return Math.floor(factor * 100);
        });

        function getPartType(label) {
            if (label.toLowerCase() === "original del") {
                return {
                    label,
                    color: "green",
                };
            }
            if (label.toLowerCase() === "økonomidel") {
                return {
                    label,
                    color: "yellow",
                };
            }

            return null;
        }

        const partType = computed(() => {
            const partTypeAttribute = (
                props.product.wm_custom_attributes || []
            ).find((item) => item.attribute_code === "ag_produktgruppe");

            if (partTypeAttribute) {
                return getPartType(partTypeAttribute.value);
            }

            return null;
        });

        return {
            cardProduct,
            image,
            imageSize,
            link,
            nuxtImgConfig,
            regularPrice,
            displayRegularPrice,
            displaySpecialPrice,
            sku,
            specialPrice,
            title,
            hasPriceRange,
            hasLabels,
            availableActions,
            recommendedNetPrice,
            arePricesIncludingTax: priceIncludesTax,
            triggerClick,
            deliveryTime,
            handleInput,
            handleAddToCart,
            showAddToCart,
            addToCartError,
            isCartLoading,
            canAddToCart,
            qty,
            addItem,
            taxOff,
            isSale,
            isNew,
            partType,
            price,
        };
    },
});
